import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import FanLoadingIcon from "components/FanLoadingIcon";
import "./TrafficPage.css";
import TimeUnitSelector from "./components/TimeUnitSelector";
import CustomDatePicker from "./components/CustomDatePicker";
import Pagination from "components/Pagination";
import { TRAFFIC_VOLUMNE_LIST } from "pages/Location/constants";
import LocationListHeader from "components/LocationListHeader";
import { standardizeString } from "utils/function";
import { trafficCache, prefetchTrafficPageData } from "./components/trafficCache";

const client = generateClient();

const TIME_FRAME = {
  TEN_MINUTES: "TEN_MINUTES",
  HOURLY: "HOURLY",
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
};

const GET_TRAFFIC_SUMMARIES = `
  query GetTrafficSummaries($timeUnit: String!, $selectedDateTime: AWSDateTime!, $sensorIds: [String!]!) {
    getTrafficSummaries(timeUnit: $timeUnit, selectedDateTime: $selectedDateTime, sensorIds: $sensorIds) {
      sensorId
      bucketTime
      totalCars
      totalPedestrians
      totalTraffic
    }
  }
`;

const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return "-";
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const getBaseSensorInfo = (sensor) => {
  let scenarioCount = 0;
  if (sensor.data) {
    for (let i = 1; i <= 20; i++) {
      if (sensor.data[`trafficVolumeScenario${i}`] === true) scenarioCount++;
    }
  }

  return {
    id: sensor.id,
    rank: 0,
    scenarioCount: scenarioCount,
    managementId: sensor.managementId || "-",
    managementArea: sensor.data?.suburb || "-",
    displayName: sensor.name || "-",
    address: sensor.location || "-",
    coordinates: formatCoordinates(sensor.lat, sensor.lng),
  };
};

const getRounded10MinISO = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  date.setMinutes(Math.floor(date.getMinutes() / 10) * 10, 0, 0);
  return date.toISOString();
};

function TrafficPage() {
  const [timeUnit, setTimeUnit] = useState(TIME_FRAME.DAILY);
  
  const initialDate = getRounded10MinISO(new Date());
  const [selectedDateTime, setSelectedDateTime] = useState(initialDate);
  const [tempDateTime, setTempDateTime] = useState(initialDate); 

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "ascending" });
  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState("");

  const ITEMS_PER_PAGE = 50;
  const navigate = useNavigate();

  const fetchIdRef = useRef(0);
  const queryCacheRef = useRef({}); 

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedDateTime !== tempDateTime) {
        setSelectedDateTime(tempDateTime);
        setCurrentPage(1);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [tempDateTime, selectedDateTime]);

  const onSearch = (originalKeyword) => {
    setKeyword(originalKeyword);
    setCurrentPage(1);
  };

  const handleTimeUnitChange = (e) => {
    setTimeUnit(e.target.value);
    setCurrentPage(1);
  };

  const fetchData = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;
    
    const cacheKey = `${timeUnit}_${selectedDateTime}`;

    if (queryCacheRef.current[cacheKey]) {
      setTableData(queryCacheRef.current[cacheKey]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!trafficCache.sensorsPromise) {
        prefetchTrafficPageData();
      }

      const sensorsToFetchTraffic = await trafficCache.sensorsPromise;

      if (!sensorsToFetchTraffic || sensorsToFetchTraffic.length === 0) {
        if (currentFetchId !== fetchIdRef.current) return;
        setTableData([]);
        setLoading(false);
        return;
      }

      const sensorIds = sensorsToFetchTraffic.map((sensor) => sensor.id);
      let trafficSummaries = [];

      const CACHE_TTL_MS = 5 * 60 * 1000; 
      const isCacheFresh = trafficCache.fetchTime && (Date.now() - trafficCache.fetchTime < CACHE_TTL_MS);

      const isUsingPrefetchCache = 
        isCacheFresh && 
        trafficCache.trafficPromise &&
        timeUnit === TIME_FRAME.DAILY &&
        trafficCache.initialDateTime &&
        selectedDateTime === getRounded10MinISO(trafficCache.initialDateTime);

      if (isUsingPrefetchCache) {
        trafficSummaries = await trafficCache.trafficPromise;
      } else {
        const summaryResult = await client.graphql({
          query: GET_TRAFFIC_SUMMARIES,
          variables: { timeUnit, selectedDateTime, sensorIds },
        });
        trafficSummaries = summaryResult.data.getTrafficSummaries || [];
      }

      if (currentFetchId !== fetchIdRef.current) return;

      let combinedData = sensorsToFetchTraffic.map((sensor) => {
        const baseInfo = getBaseSensorInfo(sensor);
        
        const summary = trafficSummaries.find((s) => s.sensorId === sensor.id) || {
          totalTraffic: 0,
          totalCars: 0,
          totalPedestrians: 0,
        };

        const searchString = standardizeString(baseInfo.managementId) + " " + 
                             standardizeString(baseInfo.managementArea) + " " + 
                             standardizeString(baseInfo.displayName) + " " + 
                             standardizeString(baseInfo.address);

        return {
          ...baseInfo,
          totalTraffic: summary.totalTraffic,
          totalCars: summary.totalCars,
          totalPedestrians: summary.totalPedestrians,
          searchString, 
        };
      });

      combinedData.sort((a, b) => b.totalTraffic - a.totalTraffic);
      const rankedData = combinedData.map((item, index) => ({
        ...item,
        rank: index + 1,
      }));

      queryCacheRef.current[cacheKey] = rankedData;
      setTableData(rankedData);
      
    } catch (err) {
      if (currentFetchId === fetchIdRef.current) {
        console.error("Error fetching or processing data:", err);
        setError(err.message || "An unexpected error occurred.");
      }
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [timeUnit, selectedDateTime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const filteredTableData = useMemo(() => {
    if (!keyword) return tableData;
    const keywords = keyword
      .trim()
      .split(/\s+/)
      .map((k) => standardizeString(k))
      .filter((k) => k !== "");

    return tableData.filter((item) => {
      for (const kw of keywords) {
        if (!item.searchString.includes(kw)) {
          return false;
        }
      }
      return true;
    });
  }, [tableData, keyword]);

  const sortedTableData = useMemo(() => {
    let sortableItems = [...filteredTableData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === "number" && typeof valB === "number") {
          if (valA < valB) return sortConfig.direction === "ascending" ? -1 : 1;
          if (valA > valB) return sortConfig.direction === "ascending" ? 1 : -1;
        } else {
          const strA = String(valA);
          const strB = String(valB);
          if (strA < strB) return sortConfig.direction === "ascending" ? -1 : 1;
          if (strA > strB) return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredTableData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedTableData.slice(startIndex, endIndex);
  }, [sortedTableData, currentPage]);

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "ascending" ? " ▲" : " ▼";
  };

  const isTimeSelectable = timeUnit === TIME_FRAME.TEN_MINUTES || timeUnit === TIME_FRAME.HOURLY;

  return (
    <div className="traffic-page-container" style={{ position: "relative" }}>
      <LocationListHeader keyword={keyword} onSearch={onSearch} selected={TRAFFIC_VOLUMNE_LIST} />
      <div className="traffic-selectors-container">
        <TimeUnitSelector timeUnit={timeUnit} onChange={handleTimeUnitChange} />

        <CustomDatePicker
          selectedDateTime={tempDateTime} 
          onChange={(date) => {
            if (date) {
              setTempDateTime(date.toISOString()); 
            }
          }}
          showTimeSelect={isTimeSelectable}
        />

        <Pagination total={sortedTableData.length} pageNum={Math.ceil(sortedTableData.length / ITEMS_PER_PAGE)} curPage={currentPage} onPageChange={(page) => setCurrentPage(page)} itemNumPerPage={ITEMS_PER_PAGE} isNew={true} />
      </div>

      {loading && (
        <div style={{ position: "absolute", top: "200px", left: "50%", transform: "translateX(-50%)", zIndex: 10 }}>
          <FanLoadingIcon />
        </div>
      )}

      {error && <div className="traffic-status-message traffic-error-message">エラー: {error}</div>}
      
      {!error && tableData.length === 0 && !loading && (
        <div className="traffic-status-message">表示するデータがありません。</div>
      )}

      {!error && tableData.length > 0 && (
        <div className={`traffic-table-wrapper ${loading ? 'loading-opacity' : ''}`}>
          <table className="traffic-table">
            <thead className="traffic-table-header">
              <tr>
                <th scope="col" className="traffic-table-th" onClick={() => requestSort("rank")}>順位{getSortDirectionIndicator("rank")}</th>
                <th scope="col" className="traffic-table-th" onClick={() => requestSort("totalTraffic")}>交通量{getSortDirectionIndicator("totalTraffic")}</th>
                <th scope="col" className="traffic-table-th">自動車合計</th>
                <th scope="col" className="traffic-table-th">歩行者合計</th>
                <th scope="col" className="traffic-table-th">シナリオ数</th>
                <th scope="col" className="traffic-table-th">管理番号</th>
                <th scope="col" className="traffic-table-th">管理エリア</th>
                <th scope="col" className="traffic-table-th">表示名</th>
                <th scope="col" className="traffic-table-th">住所</th>
                <th scope="col" className="traffic-table-th">座標</th>
              </tr>
            </thead>
            
            <tbody 
              className="traffic-table-body traffic-table-body-animated"
              key={`${timeUnit}_${selectedDateTime}_${currentPage}_${sortConfig.key}_${sortConfig.direction}`}
            >
              {paginatedData.map((item) => (
                <tr key={item.id} className="traffic-table-row">
                  <td className="traffic-table-td traffic-table-td-rank">{item.rank}</td>
                  <td className="traffic-table-td traffic-text-right">{item.totalTraffic.toLocaleString()}</td>
                  <td className="traffic-table-td traffic-text-right">{item.totalCars.toLocaleString()}</td>
                  <td className="traffic-table-td traffic-text-right">{item.totalPedestrians.toLocaleString()}</td>
                  <td className="traffic-table-td traffic-text-center">{item.scenarioCount}</td>
                  <td className="traffic-table-td traffic-table-td-link"><a href={`/base/${item.id}`}>{item.managementId}</a></td>
                  <td className="traffic-table-td">{item.managementArea}</td>
                  <td className="traffic-table-td">{item.displayName}</td>
                  <td className="traffic-table-td">{item.address}</td>
                  <td className="traffic-table-td">{item.coordinates}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TrafficPage;